/**
 * Text processing utilities for handling special characters and HTML content
 * across CMS-driven components consistently.
 */

/**
 * Properly decodes HTML entities while preserving all valid Unicode characters
 * including special symbols, emojis, Arabic text, etc.
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return "";
  
  return text
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))
    .replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)));
}

/**
 * Extracts plain text from HTML content while preserving special characters
 */
export function extractTextFromHtml(html: string): string {
  if (!html) return "";
  
  // Remove HTML tags but preserve the text content with special characters
  const textContent = html.replace(/<[^>]+>/g, "");
  return decodeHtmlEntities(textContent);
}

/**
 * Truncates HTML content at word boundaries while preserving special characters
 */
export function truncateHtmlContent(html: string, maxLength: number): string {
  if (!html) return "";
  
  const textContent = extractTextFromHtml(html);
  
  if (textContent.length <= maxLength) {
    return html;
  }
  
  // Find a good truncation point at word boundary
  const truncatedText = textContent.substring(0, maxLength);
  const lastSpaceIndex = truncatedText.lastIndexOf(" ");
  
  // If we're close to a word boundary, truncate there
  const finalLength = lastSpaceIndex > maxLength - 20 ? lastSpaceIndex : maxLength;
  
  // For HTML content, we need to be more careful about truncation
  if (html.includes("<")) {
    try {
      // Use DOM parsing for accurate HTML truncation
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      
      const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let charCount = 0;
      let node: Node | null;
      
      while ((node = walker.nextNode())) {
        const text = node.textContent || "";
        
        if (charCount + text.length <= finalLength) {
          charCount += text.length;
        } else {
          const remaining = finalLength - charCount;
          node.textContent = text.substring(0, remaining);
          
          // Remove all following text nodes
          while ((node = walker.nextNode())) {
            node.textContent = "";
          }
          break;
        }
      }
      
      return tempDiv.innerHTML;
    } catch (error) {
      console.warn("Error truncating HTML content:", error);
      // Fallback to simple truncation
      return html.substring(0, finalLength);
    }
  } else {
    // Plain text truncation
    return textContent.substring(0, finalLength);
  }
}

/**
 * Validates that text content is safe for rendering
 * Removes only actual control characters while preserving all valid Unicode
 */
export function sanitizeTextContent(text: string): string {
  if (!text) return "";
  
  // Remove only control characters (0x00-0x1F, 0x7F-0x9F) except:
  // - Tab (0x09)
  // - Line Feed (0x0A) 
  // - Carriage Return (0x0D)
  return text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, "");
}

/**
 * Ensures consistent text processing for CMS content
 */
export function processCmsText(text: string): string {
  if (!text) return "";
  
  // First decode HTML entities properly
  const decoded = decodeHtmlEntities(text);
  
  // Then sanitize only actual control characters
  return sanitizeTextContent(decoded);
}

/**
 * Checks if text contains HTML tags
 */
export function isHtmlContent(text: string): boolean {
  return /<[^>]+>/.test(text);
}

/**
 * Gets the character count of text content (excluding HTML tags)
 */
export function getTextLength(text: string): number {
  if (!text) return 0;
  return extractTextFromHtml(text).length;
}